import cx from 'classnames';
import { useWhiteLabelContext } from 'components/white-label';
import { XClose } from 'ethena-icons';
import _ from 'lodash';
import useTranslation from 'next-translate/useTranslation';
import { ReactNode, useEffect, useState } from 'react';
import { ToastContext } from './context';
import css from './Toast.module.scss';

interface IToastProps {
  children: ReactNode;
}

enum MessageType {
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

interface IAlertProps {
  message: ReactNode;
  type: MessageType;
  options: IAlertOptions;
}

interface IAlertOptions {
  autoDismiss?: boolean;
  duration?: number;
}

const DEFAULT_OPTIONS = {
  autoDismiss: true,
  duration: 5000,
};

const getDefaultMessage = (messageType: MessageType, supportEmail: string) => {
  switch (messageType) {
    case MessageType.ERROR:
      return `Oops! Something went wrong. Please try again in a few moments. If this problem persists contact ${supportEmail}.`;
    default:
      '';
  }
};

export const Toast = ({ children }: IToastProps) => {
  const { t } = useTranslation('modals');
  const { supportEmail } = useWhiteLabelContext();

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [alertProps, setAlertProps] = useState<IAlertProps>();

  const handleMessage = (
    type: MessageType,
    message: ReactNode,
    options?: IAlertOptions
  ) => {
    setAlertProps({
      message: message ?? getDefaultMessage(type, supportEmail),
      type,
      options: {
        ...DEFAULT_OPTIONS,
        ...options,
      },
    });
    setIsVisible(true);
  };

  useEffect(() => {
    if (isVisible && alertProps.options.autoDismiss) {
      const timer = setTimeout(
        () => setIsVisible(false),
        alertProps.options.duration
      );
      return () => clearTimeout(timer);
    }
  }, [isVisible, alertProps?.options?.autoDismiss]);

  const message = alertProps?.message;

  return (
    <ToastContext.Provider
      value={{
        message: {
          error: _.partial(handleMessage, MessageType.ERROR),
          success: _.partial(handleMessage, MessageType.SUCCESS),
        },
      }}
    >
      <div
        className={cx(css.toast, {
          [css.open]: isVisible,
          [css.error]: alertProps?.type === MessageType.ERROR,
          [css.success]: alertProps?.type === MessageType.SUCCESS,
        })}
      >
        <div className="d-flex">
          <p className={css.message}>
            {_.isFunction(message) ? message({ setIsVisible }) : message}
          </p>
          <button
            aria-label={t('close')}
            className={css.closeButton}
            disabled={!isVisible}
            onClick={() => setIsVisible(false)}
          >
            <XClose />
          </button>
        </div>
      </div>
      {children}
    </ToastContext.Provider>
  );
};
